import { createTheme as createMuiTheme } from "@mui/material/styles";
import { HYPHENATION_SETTINGS } from "./hyphenation.js";
import merge from "lodash-es/merge.js";
export const createTheme = ({ sansSerifFont, serifFont, }) => {
    const theme = createMuiTheme({
        sansSerifFontFamily: sansSerifFont,
        breakpoints: {
            values: {
                xs: 0,
                sm: 767,
                md: 1024,
                lg: 1300,
                xl: 2000,
            },
        },
        components: {
            MuiUseMediaQuery: {
                defaultProps: {
                    noSsr: true,
                },
            },
            MuiCssBaseline: {
                styleOverrides: `
        html, body {
          overscroll-behavior-y: none;
        }
        body {
          overflow-x: hidden;
        }
        sup {
          line-height: 1;
        }
        p {
          margin-block-start:0;
          margin-block-end:0;
        }
        a {
          color: inherit;
          text-decoration: underline;
          text-underline-offset: 0.17em;
          &:hover {
            opacity: 0.75;
          }
        }
      `,
            },
        },
        palette: {
            background: {
                default: "#fffffe",
                paper: "#f8f5f2",
            },
            primary: {
                main: "#232323",
            },
            secondary: {
                main: "#bee5f0",
            },
            info: {
                main: "#f8f5f2",
            },
            text: {
                primary: "#222525",
            },
            grey: {
                "50": "#F4F2EF",
                "100": "#E9E7E4",
                "200": "#D4D2D0",
                "300": "#BEBCBA",
                "400": "#A9A8A6",
                "500": "#939290",
                "600": "#7E7D7C",
                "700": "#696867",
                "800": "#535352",
                "900": "#3E3E3D",
                A100: "#DFDDDA",
            },
        },
        shape: { borderRadius: 4 },
        spacing: (factor) => `${0.5 * factor}rem`,
        typography: {
            fontFamily: serifFont,
            htmlFontSize: 16,
            fontSize: 24,
            fontWeightBold: 800,
            h6: undefined,
            body2: undefined,
            body3: undefined,
        },
    });
    // 👇 Typography
    theme.typography.h1 = {
        ...theme.typography.h1,
        ...HYPHENATION_SETTINGS,
        fontWeight: 400,
        fontKerning: "normal",
        fontSize: "2.5rem",
        lineHeight: "2.85rem",
        letterSpacing: "0",
        marginBottom: 0,
        marginTop: 0,
        paddingTop: 0,
        paddingBottom: 0,
        [theme.breakpoints.up("sm")]: {
            hyphens: "none",
        },
        [theme.breakpoints.up("md")]: {
            fontSize: "2.75rem",
            lineHeight: "3.25rem",
            hyphens: "none",
        },
        [theme.breakpoints.up("lg")]: {
            fontSize: "3.5rem",
            lineHeight: "4rem",
            hyphens: "none",
        },
        [theme.breakpoints.up("xl")]: {
            fontSize: "3.75rem",
            lineHeight: "4.25rem",
            hyphens: "none",
        },
    };
    theme.typography.h2 = {
        ...theme.typography.h2,
        ...HYPHENATION_SETTINGS,
        fontWeight: 400,
        fontSize: "2rem",
        fontKerning: "normal",
        letterSpacing: "0",
        lineHeight: "2.5rem",
        marginBottom: 0,
        marginTop: 0,
        paddingTop: 0,
        paddingBottom: 0,
        [theme.breakpoints.up("md")]: {
            fontSize: "2.25rem",
            letterSpacing: "0",
            lineHeight: "2.75rem",
            hyphens: "none",
        },
        [theme.breakpoints.up("lg")]: {
            fontSize: "2.75rem",
            letterSpacing: "0",
            lineHeight: "3.25rem",
            hyphens: "none",
        },
        [theme.breakpoints.up("xl")]: {
            fontSize: "3rem",
            letterSpacing: "0",
            lineHeight: "3.5rem",
            hyphens: "none",
        },
    };
    theme.typography.h3 = {
        ...theme.typography.h3,
        ...HYPHENATION_SETTINGS,
        fontWeight: 400,
        fontKerning: "normal",
        fontSize: "1.75rem",
        letterSpacing: "0",
        lineHeight: "2.25rem",
        marginBottom: 0,
        marginTop: 0,
        paddingTop: 0,
        paddingBottom: 0,
        [theme.breakpoints.up("sm")]: {
            hyphens: "none",
        },
        [theme.breakpoints.up("lg")]: {
            fontSize: "2.5rem",
            letterSpacing: "0",
            lineHeight: "3rem",
            hyphens: "none",
        },
        [theme.breakpoints.up("xl")]: {},
    };
    theme.typography.h4 = {
        ...theme.typography.h4,
        ...HYPHENATION_SETTINGS,
        fontWeight: 500,
        fontSize: "1.35rem",
        fontKerning: "normal",
        lineHeight: "2rem",
        letterSpacing: "0",
        marginBottom: 0,
        marginTop: 0,
        paddingTop: 0,
        paddingBottom: 0,
        [theme.breakpoints.up("sm")]: {
            hyphens: "none",
        },
        [theme.breakpoints.up("lg")]: {
            fontSize: "1.875rem",
            fontWeight: 400,
            hyphens: "none",
            letterSpacing: "0",
            lineHeight: "2.625rem",
        },
        [theme.breakpoints.up("xl")]: {},
    };
    theme.typography.h5 = {
        ...theme.typography.h5,
        display: "inline-block",
        fontFamily: theme.sansSerifFontFamily,
        fontKerning: "normal",
        fontSize: "0.8rem",
        lineHeight: 1.5,
        letterSpacing: "0.14em",
        paddingTop: 0,
        paddingBottom: 0,
        textTransform: "uppercase",
        [theme.breakpoints.up("sm")]: {
            hyphens: "none",
        },
        [theme.breakpoints.up("lg")]: {
            fontSize: "1rem",
            lineHeight: 1.5,
        },
    };
    theme.typography.overline = {
        ...theme.typography.h5,
        paddingBottom: theme.spacing(0.35),
    };
    theme.typography.body1 = {
        ...theme.typography.body1,
        fontSize: "1.25rem",
        lineHeight: "1.85rem",
        [theme.breakpoints.up("lg")]: {
            fontSize: "1.45rem",
            lineHeight: "2.2rem",
        },
        [theme.breakpoints.up("xl")]: {
            fontSize: "1.5rem",
            lineHeight: "2.25rem",
        },
        "&.MuiTypography-paragraph": {
            marginBottom: theme.spacing(3),
        },
        "&.hyphenate": {
            ...HYPHENATION_SETTINGS,
        },
    };
    theme.typography.body2 = {
        ...theme.typography.body2,
        fontSize: "1.05rem",
        lineHeight: "1.5rem",
        [theme.breakpoints.up("md")]: {
            fontSize: "1.25rem",
            lineHeight: "1.65rem",
        },
        [theme.breakpoints.up("lg")]: {
            fontSize: "1.35rem",
            lineHeight: "2rem",
        },
        "&.hyphenate": {
            ...HYPHENATION_SETTINGS,
        },
    };
    theme.typography.caption = {
        ...theme.typography.caption,
        fontSize: "0.85rem",
        lineHeight: "1.3rem",
        [theme.breakpoints.up("md")]: {
            fontSize: "1rem",
            lineHeight: "1.65rem",
        },
        [theme.breakpoints.up("lg")]: {
            fontSize: "1.15rem",
            lineHeight: "1.75rem",
        },
    };
    theme.typography.body3 = {
        ...theme.typography.body2,
        fontFamily: theme.sansSerifFontFamily,
        fontWeight: 300,
    };
    theme.typography.subtitle1 = {
        ...theme.typography.h5,
        fontWeight: 500,
        letterSpacing: "0.025em",
        textTransform: "none",
    };
    theme.typography.subtitle2 = {
        letterSpacing: "0.015em",
        fontWeight: 500,
        display: "inline-block",
        fontFamily: theme.sansSerifFontFamily,
        fontKerning: "normal",
        fontSize: "0.95rem",
        lineHeight: 1,
        paddingTop: 0,
        paddingBottom: 0,
        textTransform: "none",
        [theme.breakpoints.up("xs")]: {
            fontSize: "0.85rem",
            lineHeight: 1,
        },
        [theme.breakpoints.up("sm")]: {
            fontSize: "0.95rem",
            lineHeight: 1,
        },
        [theme.breakpoints.up("lg")]: {
            fontSize: "1.1rem",
            lineHeight: 1,
        },
    };
    // 👇 Shadows
    theme.shadows = {
        ...theme.shadows,
        sectionVertical: "rgba(0, 0, 0, 0.08) 0px -8px 12px -4px, rgba(0, 0, 0, 0.04) 0px -4px 6px -2px, rgba(0, 0, 0, 0.08) 0px 8px 12px -4px, rgba(0, 0, 0, 0.04) 0px 4px 6px -2px",
        sectionTop: "rgba(0, 0, 0, 0.08) 0px -8px 12px -4px, rgba(0, 0, 0, 0.04) 0px -4px 6px -2px",
        sectionBottom: "rgba(0, 0, 0, 0.08) 0px 8px 12px -4px, rgba(0, 0, 0, 0.04) 0px 4px 6px -2px",
        [0]: "0px 0px 0px 0px rgba(0,0,0,0),0px 0px 0px 0px rgba(0,0,0,0),0px 0px 0px 0px rgba(0,0,0,0)",
        [4]: "0px 2px 4px -1px rgba(0,0,0,0.2),0px 4px 5px 0px rgba(0,0,0,0.14),0px 1px 10px 0px rgba(0,0,0,0.12)",
        product: `
        2.8px 0.7px 2.2px rgba(0, 0, 0, 0.02),
        6.7px 1.7px 5.3px rgba(0, 0, 0, 0.028),
        12.5px 3.1px 10px rgba(0, 0, 0, 0.035),
        22.3px 5.6px 17.9px rgba(0, 0, 0, 0.042),
        41.8px 10.4px 33.4px rgba(0, 0, 0, 0.05),
        100px 25px 80px rgba(0, 0, 0, 0.07)
        `,
        productHover: `
        2.8px 2.8px 2.2px rgba(0, 0, 0, 0.02),
          6.7px 6.7px 5.3px rgba(0, 0, 0, 0.028),
          12.5px 12.5px 10px rgba(0, 0, 0, 0.035),
          22.3px 22.3px 17.9px rgba(0, 0, 0, 0.042),
          41.8px 41.8px 33.4px rgba(0, 0, 0, 0.05),
          100px 100px 80px rgba(0, 0, 0, 0.07)
        `,
    };
    // 👇 Alert
    theme.components = merge(theme.components, {
        MuiAlert: {
            styleOverrides: {
                icon: {
                    padding: "9px 0",
                    [theme.breakpoints.up("md")]: {
                        padding: "10px 0",
                    },
                    [theme.breakpoints.up("xl")]: {
                        padding: "13px 0",
                    },
                },
                message: {
                    fontSize: "1rem",
                    lineHeight: 1.5,
                    fontFamily: theme.sansSerifFontFamily,
                    [theme.breakpoints.up("md")]: {
                        fontSize: "1.15rem",
                        lineHeight: 1.5,
                    },
                    [theme.breakpoints.up("xl")]: {
                        fontSize: "1.25rem",
                        lineHeight: 1.6,
                    },
                },
                root: {},
            },
        },
    });
    // 👇 Button
    theme.components.MuiButton = {
        defaultProps: {
            disableElevation: true,
        },
        styleOverrides: {
            sizeSmall: {
                fontSize: "0.85rem",
                lineHeight: 1,
                padding: `${theme.spacing(1)} ${theme.spacing(2)}`,
                [theme.breakpoints.up("lg")]: {
                    padding: `${theme.spacing(1.25)} ${theme.spacing(2.5)}`,
                    fontSize: "1rem",
                    lineHeight: 1.1,
                },
            },
            sizeMedium: {
                fontSize: "1rem",
                lineHeight: 1.1,
                padding: `${theme.spacing(1.5)} ${theme.spacing(3)}`,
                [theme.breakpoints.up("lg")]: {
                    fontSize: "1.125rem",
                    lineHeight: 1.3,
                    padding: `${theme.spacing(1.75)} ${theme.spacing(4)}`,
                },
            },
            sizeLarge: {
                padding: `${theme.spacing(1.75)} ${theme.spacing(3.5)}`,
                [theme.breakpoints.up("lg")]: {
                    padding: `${theme.spacing(1.75)} ${theme.spacing(4)}`,
                },
            },
            root: {
                fontFamily: theme.sansSerifFontFamily,
                fontWeight: 500,
                letterSpacing: "0.075em",
                textTransform: "none",
                borderRadius: "8px",
                [theme.breakpoints.up("sm")]: {
                    borderRadius: "10px",
                },
                [theme.breakpoints.up("lg")]: {
                    borderRadius: "12px",
                    letterSpacing: "0.15em",
                },
            },
        },
    };
    // 👇 Chip
    theme.components = merge(theme.components, {
        MuiChip: {
            styleOverrides: {
                label: {
                    fontSize: "0.8rem",
                    fontFamily: theme.sansSerifFontFamily,
                    fontWeight: 500,
                    [theme.breakpoints.up("sm")]: { fontSize: "0.9rem" },
                    [theme.breakpoints.up("lg")]: {
                        fontSize: "1rem",
                        letterSpacing: "0.075em",
                        lineHeight: 1,
                    },
                },
            },
        },
    });
    // 👇 Container
    theme.components.MuiContainer = {
        styleOverrides: {
            root: theme.unstable_sx({
                px: {
                    xs: 3,
                    sm: 4,
                    md: 4,
                    lg: 6,
                    xl: 8,
                },
            }),
            maxWidthXl: theme.unstable_sx({
                maxWidth: {
                    sm: theme.breakpoints.values.md,
                    md: theme.breakpoints.values.md,
                    lg: theme.breakpoints.values.lg,
                    xl: "1600px",
                },
            }),
            disableGutters: theme.unstable_sx({
                px: {
                    xs: 0,
                    sm: 0,
                    md: 0,
                    lg: 0,
                    xl: 0,
                },
            }),
        },
    };
    // 👇 FormControlLabel
    theme.components = merge(theme.components, {
        MuiFormControlLabel: {
            styleOverrides: {
                root: {
                    alignItems: "baseline",
                    ".MuiSwitch-root": {
                        bottom: "-3px",
                        [theme.breakpoints.up("md")]: { bottom: "-1px" },
                    },
                    ".MuiFormControlLabel-label": { ...theme.typography.h5 },
                },
            },
        },
    });
    // 👇 Link
    theme.components = merge(theme.components, {
        MuiLink: {
            styleOverrides: {
                root: {
                    wordBreak: "break-all",
                },
            },
        },
    });
    // 👇 Paper
    theme.components = merge(theme.components, { MuiPaper: { defaultProps: { elevation: 0 } } });
    theme.components.MuiFilledInput = {
        styleOverrides: {
            root: {
                backgroundColor: theme.palette.background.paper,
                "&:hover": { backgroundColor: "#EDEBE8" },
            },
            focused: {
                backgroundColor: "#EDEBE8",
            },
            input: {
                fontFamily: theme.sansSerifFontFamily,
            },
        },
    };
    // 👇 Tooltip
    theme.components = merge(theme.components, {
        MuiTooltip: {
            defaultProps: {
                arrow: true,
            },
            styleOverrides: {
                tooltip: {
                    fontFamily: theme.sansSerifFontFamily,
                    fontSize: "0.85rem",
                    lineHeight: 1.25,
                },
            },
        },
    });
    return theme;
};
//# sourceMappingURL=create-theme.js.map